/**
 * The only place stored JSON becomes a card document.
 *
 * Everything in the browser origin can write to the storage key — devtools,
 * another module, a stale schema from an earlier build. Parsing that straight
 * into the reducer is how an editor crashes or renders phantom blocks (threat
 * model, abuse case 1), so nothing here trusts a shape: unknown field ids are
 * dropped, every enum is clamped to its own values, text is length-capped, and
 * a document that cannot be rescued is skipped rather than rendered.
 *
 * `normalizeCard` then re-establishes the structural invariants, so the result
 * is always a document the rest of the module can rely on.
 */

import {
  CTA_FIELD_ID,
  DESC_FIELD_ID,
  MAX_CUSTOM_CSS_LENGTH,
  MAX_DISPLAY_LABEL_LENGTH,
  isApplicationRef,
  isDescriptionStyle,
  isImageFit,
  isKnownFieldId,
  isPhotoAnchor,
  isRegionId,
  rendersBlock,
} from '../constants';
import { clampText, normalizeCard, normalizeCtaConfiguration } from '../state';
import {
  ApplicationRef,
  BlockAnchor,
  BlockPlacement,
  CardOrientation,
  CtaConfiguration,
  FieldAlignment,
  FieldId,
  FieldConfiguration,
  ProductCard,
} from '../types';

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asString = (value: unknown, fallback: string): string =>
  typeof value === 'string' ? value : fallback;

const asBoolean = (value: unknown, fallback: boolean): boolean =>
  typeof value === 'boolean' ? value : fallback;

const asFiniteNumber = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const asOrientation = (value: unknown): CardOrientation =>
  value === 'horizontal' ? 'horizontal' : 'vertical';

const asAlignment = (value: unknown): FieldAlignment =>
  value === 'center' || value === 'right' ? value : 'left';

const asFieldIds = (value: unknown): FieldId[] => asArray(value).filter(isKnownFieldId);

const asReferences = (value: unknown): ApplicationRef[] => {
  const seen = new Set<ApplicationRef>();
  asArray(value).forEach((candidate) => {
    if (isApplicationRef(candidate)) {
      seen.add(candidate);
    }
  });
  return Array.from(seen);
};

const asAnchor = (value: unknown): BlockAnchor | undefined => {
  if (isPhotoAnchor(value)) {
    return value;
  }
  return value === 'sticky' ? 'sticky' : undefined;
};

const asPlacements = (value: unknown): BlockPlacement[] => {
  const placements: BlockPlacement[] = [];
  asArray(value).forEach((candidate) => {
    if (!isRecord(candidate)) {
      return;
    }
    const fieldId = candidate.fieldId;
    if (!isKnownFieldId(fieldId)) {
      return;
    }
    const region = isRegionId(candidate.region) ? candidate.region : 'info';
    const rowKey = candidate.rowKey;
    placements.push({
      fieldId,
      region,
      order: asFiniteNumber(candidate.order, 0),
      rowKey: typeof rowKey === 'string' ? clampText(rowKey, 80) : undefined,
      anchor: asAnchor(candidate.anchor),
    });
  });
  return placements;
};

/**
 * A stored CTA options object → options in range (spec R70).
 *
 * Every value is coerced to its own type here and then clamped by
 * `normalizeCtaConfiguration`, so a `widthPct` of `1e9` or a `showText` of `"yes"` from
 * an older build cannot lay the preview out off-screen (threat model, abuse case 2).
 */
const asCtaConfiguration = (value: unknown): CtaConfiguration | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }
  const partial: Partial<CtaConfiguration> = {
    label: typeof value.label === 'string' ? value.label : undefined,
    showText: typeof value.showText === 'boolean' ? value.showText : undefined,
    showIcon: typeof value.showIcon === 'boolean' ? value.showIcon : undefined,
    fullWidth: typeof value.fullWidth === 'boolean' ? value.fullWidth : undefined,
    widthPct: typeof value.widthPct === 'number' ? value.widthPct : undefined,
    heightPx: typeof value.heightPx === 'number' ? value.heightPx : undefined,
  };
  return normalizeCtaConfiguration(partial);
};

/**
 * Same key-by-key rebuild as `normalizeConfiguration`, on a shape nothing has vouched
 * for yet. A key added to `FieldConfiguration` has to be listed **here too**, or it is
 * dropped on the first load from storage.
 */
const asFieldConfiguration = (fieldId: FieldId, value: unknown): FieldConfiguration => {
  const stored: UnknownRecord = isRecord(value) ? value : {};
  const customCss = asString(stored.customCss, '');
  return {
    displayLabel: clampText(asString(stored.displayLabel, ''), MAX_DISPLAY_LABEL_LENGTH),
    alignment: asAlignment(stored.alignment),
    // Absent for every field but the image, and dropped unless it is one of
    // the enum's own values — the reader falls back to the default (ADR-2D).
    imageFit: isImageFit(stored.imageFit) ? stored.imageFit : undefined,
    // Kept as text and only length-capped (R67): the stored form is the user's
    // own typing, and `styling/` is what confines it at render time (ADR-4A).
    customCss: customCss === '' ? undefined : clampText(customCss, MAX_CUSTOM_CSS_LENGTH),
    descStyle:
      fieldId === DESC_FIELD_ID && isDescriptionStyle(stored.descStyle)
        ? stored.descStyle
        : undefined,
    cta: fieldId === CTA_FIELD_ID ? asCtaConfiguration(stored.cta) : undefined,
    // Only a real `true` survives: anything else (a string "true", a 1) means off,
    // because this value becomes a rendering decision and must not be guessed at.
    strikethrough: stored.strikethrough === true ? true : undefined,
    showUom: stored.showUom === true ? true : undefined,
  };
};

const asConfiguration = (value: unknown): Partial<Record<FieldId, FieldConfiguration>> => {
  const configuration: Partial<Record<FieldId, FieldConfiguration>> = {};
  if (!isRecord(value)) {
    return configuration;
  }
  Object.keys(value).forEach((key) => {
    if (!isKnownFieldId(key) || !isRecord(value[key])) {
      return;
    }
    configuration[key] = asFieldConfiguration(key, value[key]);
  });
  return configuration;
};

/**
 * The published `components` shape → the editor's three internal structures.
 *
 * The wire document is deliberately denormalized — one self-contained entry per
 * enabled field (operator decision 2026-08-18) — while the editor keeps its
 * tested placements/configuration model; this is where the two meet on the way
 * in (`encode.ts` is the way out). Duplicate fieldIds keep the first occurrence,
 * matching decode's duplicate-id rule. Blockless fields (the image is the photo
 * region itself; the unit of measure only suffixes prices) contribute
 * configuration but never a placement, whatever region the document claims.
 */
const fromComponents = (
  value: unknown[]
): Pick<ProductCard, 'enabledFieldIds' | 'placements' | 'configuration'> => {
  const enabledFieldIds: FieldId[] = [];
  const placements: BlockPlacement[] = [];
  const configuration: Partial<Record<FieldId, FieldConfiguration>> = {};
  const seen = new Set<FieldId>();

  value.forEach((candidate) => {
    if (!isRecord(candidate)) {
      return;
    }
    const fieldId = candidate.fieldId;
    if (!isKnownFieldId(fieldId) || seen.has(fieldId)) {
      return;
    }
    seen.add(fieldId);
    enabledFieldIds.push(fieldId);
    configuration[fieldId] = asFieldConfiguration(fieldId, candidate.configuration);
    if (rendersBlock(fieldId) && candidate.region !== undefined) {
      const rowKey = candidate.rowKey;
      placements.push({
        fieldId,
        region: isRegionId(candidate.region) ? candidate.region : 'info',
        order: asFiniteNumber(candidate.order, 0),
        rowKey: typeof rowKey === 'string' ? clampText(rowKey, 80) : undefined,
        anchor: asAnchor(candidate.anchor),
      });
    }
  });

  return { enabledFieldIds, placements, configuration };
};

const ISO_TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}/;

const asTimestamp = (value: unknown, fallback: string): string => {
  const candidate = asString(value, '');
  return ISO_TIMESTAMP_PATTERN.test(candidate) ? candidate : fallback;
};

/** One stored entry → a usable card, or null when it has no usable identity. */
export const coerceCard = (value: unknown, fallbackTimestamp: string): ProductCard | null => {
  if (!isRecord(value)) {
    return null;
  }
  const id = asString(value.id, '').trim();
  if (id === '') {
    return null;
  }

  // The published shape is `components` (denormalized, one entry per enabled
  // field). The split enabledFieldIds/placements/configuration shape is still
  // READ — browser-storage documents from earlier builds are on people's
  // machines — but never written; only one shape is honoured per document.
  const structure = Array.isArray(value.components)
    ? fromComponents(value.components)
    : {
        enabledFieldIds: asFieldIds(value.enabledFieldIds),
        placements: asPlacements(value.placements),
        configuration: asConfiguration(value.configuration),
      };

  return normalizeCard({
    id,
    name: asString(value.name, 'Untitled card'),
    orientation: asOrientation(value.orientation),
    footerEnabled: asBoolean(value.footerEnabled, false),
    fieldOrder: asFieldIds(value.fieldOrder),
    ...structure,
    // Card-wide UOM (R69): only a real `true` turns it on.
    references: asReferences(value.references),
    updatedAt: asTimestamp(value.updatedAt, fallbackTimestamp),
  });
};

/**
 * Parses the raw storage value into cards. A malformed payload yields an empty
 * list — never a throw, and never a partially-typed document.
 */
export const parseStoredCards = (raw: string | null, fallbackTimestamp: string): ProductCard[] => {
  if (raw === null || raw === '') {
    return [];
  }

  let decoded: unknown;
  try {
    decoded = JSON.parse(raw);
  } catch {
    return [];
  }

  const seenIds = new Set<string>();
  const cards: ProductCard[] = [];
  asArray(decoded).forEach((candidate) => {
    const card = coerceCard(candidate, fallbackTimestamp);
    if (!card || seenIds.has(card.id)) {
      return;
    }
    seenIds.add(card.id);
    cards.push(card);
  });
  return cards;
};
